import { useEffect, useRef, useState } from 'react';
import { useInView } from 'framer-motion';

interface CountUpProps {
  /** Target value to count up to. */
  target: number;
  /** Delay before the count-up begins (after entering view), in seconds. */
  delay?: number;
  /** Duration of the count-up itself, in seconds. */
  duration?: number;
  /** Format a numeric value into its display string (e.g. `formatARR`). */
  formatter: (value: number) => string;
}

/**
 * Animates a number rolling from 0 → target when scrolled into view.
 *
 * Uses `useInView` to defer the count-up until the element enters the
 * viewport, then drives it with requestAnimationFrame on an ease-out-cubic
 * curve — the financial-dashboard "settle" cadence rather than a flat
 * linear count. Once triggered the count plays through to target and
 * doesn't replay. Skips animation entirely when the user prefers reduced
 * motion (jumps straight to the target).
 */
export function CountUp({
  target,
  delay = 0,
  duration = 1.2,
  formatter,
}: CountUpProps) {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.4 });

  useEffect(() => {
    if (!isInView) return;

    if (
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setValue(target);
      return;
    }

    let raf = 0;
    const timeoutId = window.setTimeout(() => {
      const start = performance.now();
      const tick = (now: number) => {
        const elapsed = (now - start) / 1000;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setValue(target * eased);
        if (progress < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, delay * 1000);

    return () => {
      window.clearTimeout(timeoutId);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [isInView, target, delay, duration]);

  return <span ref={ref}>{formatter(value)}</span>;
}
